import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Spinner } from "@/components/common";
import { toast } from "sonner";
import { ShieldCheck, UserCheck, UserX, Users, UserPlus } from "lucide-react";

const ADMIN_EMAIL = "sent584@gmail.com";

export default function AdminUsers() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "Admin" || (user?.email || "").toLowerCase() === ADMIN_EMAIL;

  const [inviteOpen, setInviteOpen] = useState(false);
  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState("Consultant");

  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
    enabled: isAdmin,
  });

  const toggle = useMutation({
    mutationFn: async ({ uid, active }) => (await api.put(`/admin/users/${uid}/active`, { active })).data,
    onSuccess: (_d, v) => { toast.success(v.active ? "User activated" : "User deactivated"); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Action failed"),
  });

  const sendInvite = async () => {
    if (!invEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    try {
      const r = await api.post("/admin/invites", { email: invEmail, role: invRole });
      setInviteOpen(false); setInvEmail("");
      if (r.data.email_sent) toast.success(`Invitation emailed to ${r.data.email}`);
      else { navigator.clipboard?.writeText(r.data.link); toast.success("Invite link copied to clipboard (email not configured)"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create invite"); }
  };

  if (!isAdmin) return <Navigate to="/" replace />;
  if (isLoading) return <Spinner label="Loading users…" />;

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-[#0f2557] text-white grid place-items-center"><ShieldCheck size={20} /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#0f2557] dark:text-[#29abe2]">User management</h1>
          <p className="text-sm text-muted-foreground">Admin oversight — every project and meeting across all users. Deactivate access instantly.</p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="invite-user-btn"><UserPlus size={15} /> Invite user</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite a user</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="name@company.com" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} data-testid="invite-email" />
              <select value={invRole} onChange={(e) => setInvRole(e.target.value)} className="w-full h-10 rounded-md border bg-background px-3 text-sm" data-testid="invite-role">
                {["Consultant", "Engagement Lead", "Client Stakeholder", "Viewer", "Admin"].map((r) => <option key={r}>{r}</option>)}
              </select>
              <p className="text-xs text-muted-foreground">A branded one-click sign-up link is emailed with this role preset. If email isn't configured, the link is copied to your clipboard.</p>
            </div>
            <DialogFooter><Button onClick={sendInvite} data-testid="send-invite-btn">Send invite</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b text-sm font-semibold text-[#0f2557] dark:text-[#29abe2]"><Users size={16} /> All users ({users?.length || 0})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b bg-secondary/40">
              <th className="py-2.5 px-5 font-medium">Name</th><th className="px-3 font-medium">Email</th>
              <th className="px-3 font-medium">Role</th><th className="px-3 font-medium">Projects</th>
              <th className="px-3 font-medium">Status</th><th className="px-5 font-medium text-right">Action</th>
            </tr></thead>
            <tbody>
              {(users || []).map((u) => {
                const primary = (u.email || "").toLowerCase() === ADMIN_EMAIL;
                const active = u.active !== false;
                return (
                  <tr key={u.user_id} className="border-b border-border/40" data-testid={`user-row-${u.user_id}`}>
                    <td className="py-2.5 px-5 font-medium text-foreground">{u.name}{primary && <span className="ml-2 text-[10px] bg-[#c9a227]/15 text-[#a6821a] px-1.5 py-0.5 rounded">Primary admin</span>}</td>
                    <td className="px-3 text-muted-foreground">{u.email}</td>
                    <td className="px-3">{u.role}</td>
                    <td className="px-3">{u.project_count ?? 0}</td>
                    <td className="px-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${active ? "bg-emerald-500/15 text-emerald-600" : "bg-red-500/15 text-red-500"}`}>{active ? "Active" : "Deactivated"}</span>
                    </td>
                    <td className="px-5 text-right">
                      <Button size="sm" variant={active ? "outline" : "default"} disabled={primary || toggle.isPending}
                        onClick={() => toggle.mutate({ uid: u.user_id, active: !active })}
                        className="gap-1.5 h-8" data-testid={`toggle-user-${u.user_id}`}>
                        {active ? <><UserX size={13} /> Deactivate</> : <><UserCheck size={13} /> Activate</>}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
